import React, { useMemo, useState } from 'react';
import {
  HIERARCHY_LEVELS,
  HIERARCHY_LABEL,
  formatMentorName,
  eligibleSupervisors,
  outrankedReports,
} from './hierarchyUtils';

/**
 * Edit one mentor's level and supervisor.
 *
 * The supervisor dropdown only offers legal choices (strictly higher rank, not
 * in the mentor's own downline) so invalid picks aren't presented. The server
 * re-validates every request regardless.
 */
const EditHierarchyModal = ({ mentor, allMentors, onClose, onSave, onUnlock, saving }) => {
  const [hierarchy, setHierarchy] = useState(mentor.hierarchy);
  const [supervisorId, setSupervisorId] = useState(
    mentor.supervisorId == null ? '' : String(mentor.supervisorId)
  );
  const [supervisorSearch, setSupervisorSearch] = useState('');

  const candidates = useMemo(
    () => eligibleSupervisors(allMentors, mentor, hierarchy),
    [allMentors, mentor, hierarchy]
  );

  const visibleCandidates = useMemo(() => {
    const q = supervisorSearch.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => formatMentorName(c).toLowerCase().includes(q));
  }, [candidates, supervisorSearch]);

  // If a level change makes the currently-selected supervisor illegal, surface
  // it rather than silently submitting something the server will reject.
  const selectedStillValid =
    supervisorId === '' || candidates.some((c) => String(c.id) === supervisorId);

  const conflicts = outrankedReports(mentor, hierarchy);

  const dirty =
    hierarchy !== mentor.hierarchy ||
    supervisorId !== (mentor.supervisorId == null ? '' : String(mentor.supervisorId));

  const blocker = !selectedStillValid
    ? 'The selected supervisor no longer outranks this mentor. Pick another or clear it.'
    : conflicts.length > 0
    ? `These reports would meet or outrank this mentor: ${conflicts
        .map(formatMentorName)
        .join(', ')}. Reassign them first.`
    : null;

  const submit = (e) => {
    e.preventDefault();
    if (blocker || !dirty) return;
    const changes = {};
    if (hierarchy !== mentor.hierarchy) changes.hierarchy = hierarchy;
    const nextSupervisor = supervisorId === '' ? null : parseInt(supervisorId, 10);
    if (nextSupervisor !== (mentor.supervisorId ?? null)) {
      changes.supervisorId = nextSupervisor;
    }
    onSave(mentor.id, changes);
  };

  return (
    <div className="hierarchy-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="hierarchy-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit hierarchy for ${formatMentorName(mentor)}`}
      >
        <div className="hierarchy-modal-header">
          <h3>{formatMentorName(mentor)}</h3>
          <button type="button" className="hierarchy-modal-close" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <form onSubmit={submit} className="hierarchy-modal-body">
          <div className="hierarchy-field">
            <label htmlFor="edit-hierarchy-level">Hierarchy level</label>
            <select
              id="edit-hierarchy-level"
              value={hierarchy}
              onChange={(e) => setHierarchy(e.target.value)}
            >
              {HIERARCHY_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {HIERARCHY_LABEL[level]}
                </option>
              ))}
            </select>
          </div>

          <div className="hierarchy-field">
            <label htmlFor="edit-hierarchy-supervisor">Supervisor</label>
            <input
              type="text"
              className="hierarchy-supervisor-search"
              placeholder="Search supervisors…"
              value={supervisorSearch}
              onChange={(e) => setSupervisorSearch(e.target.value)}
              aria-label="Search supervisors"
            />
            <select
              id="edit-hierarchy-supervisor"
              value={supervisorId}
              onChange={(e) => setSupervisorId(e.target.value)}
              size={6}
            >
              <option value="">— No supervisor —</option>
              {visibleCandidates.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {formatMentorName(c)} · {HIERARCHY_LABEL[c.hierarchy]}
                </option>
              ))}
            </select>
            {candidates.length === 0 && (
              <small className="hierarchy-field-hint">
                No mentor outranks {HIERARCHY_LABEL[hierarchy]}, so no supervisor can be assigned.
              </small>
            )}
          </div>

          {blocker && <div className="hierarchy-modal-error">{blocker}</div>}

          <div className="hierarchy-modal-lock">
            {mentor.manuallyAssigned ? (
              <>
                <i className="bi bi-lock-fill" /> Locked — the Testpress sync will not change this
                mentor&rsquo;s hierarchy.
                <button
                  type="button"
                  className="hierarchy-unlock-btn"
                  onClick={() => onUnlock(mentor.id)}
                  disabled={saving}
                >
                  Unlock &amp; let sync manage it
                </button>
              </>
            ) : (
              <>
                <i className="bi bi-unlock" /> Currently managed by the Testpress sync. Saving will
                lock this mentor so your change survives future syncs.
              </>
            )}
          </div>

          <div className="hierarchy-modal-footer">
            <button type="button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="hierarchy-save-btn" disabled={saving || !dirty || !!blocker}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditHierarchyModal;
